import Cookie from 'js-cookie';
import 'regenerator-runtime/runtime.js';

class IubendaProvider {
    constructor() {
        this._consent = null;
    }

    /**
     * @returns {boolean}
     */
    hasIubenda() {
        return this._hasIubendaScript()
            && this._hasIubendaConfiguration();
    }

    /**
     * @returns {boolean}
     */
    hasConsent() {
        return this._consent !== null;
    }

    /**
     * @param {boolean} consent
     */
    setConsent(consent) {
        // Не допускает повторного принятия решения
        if (this.hasConsent()) {
            console.warn('Consent already have');

            return null;
        }

        this._consent = consent;

        if (consent) {
            handleTrackClick();
        }

        this._dispatchConsent();
        this._stopWaitConsentCookie();
    }

    startWaitConsentCookie() {
        if (this.hasConsent()) {
            this._dispatchConsent();
            return null;
        }

        this._waitConsentInterval = setInterval(this._checkCookiesConsent.bind(this), 500);

        console.info('Start wait consent');
    }

    /**
     * @private
     */
    _dispatchConsent() {
        if (this._consent === true) {
            document.dispatchEvent(new CustomEvent('iubenda.consent', {
                cancelable: false
            }));
        }
    }

    /**
     * @see https://www.iubenda.com/en/help/1205-how-to-configure-your-cookie-solution-advanced-guide#preference-cookie
     * @private
     */
    _checkCookiesConsent() {
        if (this.hasConsent()) {
            this._stopWaitConsentCookie();
        }

        let consent = null;
        const cookiePolicyId = _iub.csConfiguration.cookiePolicyId;
        const possibleCookieNames = [
            '_iub_cs-s' + cookiePolicyId,
            '_iub_cs-' + cookiePolicyId
        ]

        possibleCookieNames.map(cookieName => {
            const cookieValue = Cookie.get(cookieName);
            if (typeof cookieValue !== 'undefined') {
                const consentObject = JSON.parse(cookieValue);
                consent = consentObject.consent;
            }
        });

        if (consent !== null) {
            this.setConsent(consent);
        }

        // В случае, если Iubenda не устанавливает куки и не вызывает callback,
        // но при этом не требуется решения по GDPR
        if (!this._isNeedConsent()) {
            console.info('Consent is not needed');

            this.setConsent(true);
        }
    }

    /**
     * @private
     */
    _stopWaitConsentCookie() {
        if (typeof this._waitConsentInterval !== 'undefined') {
            clearInterval(this._waitConsentInterval);

            console.info('Stop wait consent');
        }
    }

    /**
     * @returns {boolean}
     * @private
     */
    _hasIubendaConfiguration() {
        return typeof _iub !== 'undefined'
            && typeof _iub.csConfiguration !== 'undefined';
    }

    /**
     * @returns {boolean}
     * @private
     */
    _hasIubendaScript() {
        return document.querySelector('script[src*="iubenda.com"]') !== null;
    }

    /**
     * @returns {boolean}
     * @private
     */
    _isNeedConsent() {
        if (!this._isStateAvailable()) {
            return true;
        }

        if (typeof _iub.cs.state.needsConsent !== 'boolean') {
            return true;
        }

        return _iub.cs.state.needsConsent !== false;
    }

    /**
     * @returns {boolean}
     * @private
     */
    _isStateAvailable() {
        return typeof _iub.cs !== 'undefined'
            && typeof _iub.cs.state !== 'undefined';
    }
}

class ClickContextProvider {
    _getScriptUrl() {
        const element = document.querySelector('script[src*="/click.js"]');
        return element.src;
    }

    _getHostFromUrl(url) {
        //Все что после протокола (если есть) до первого слеша
        const matches = url.match(/^([^:]+:\/\/||\/\/)?([^\/]+)/i);
        if (matches === null) {
            return window.location.host;
        }

        return matches[2];
    }

    getClickClientId() {
        const clientId = Cookie.get('clicks_client_id');

        if (typeof clientId !== 'undefined') {
            this.dispatchClientId(clientId);
        }

        return clientId;
    }

    dispatchClientId(clientId) {
        document.dispatchEvent(new CustomEvent('client_id.post_set', {
            detail: {
                clientId: clientId
            },
            cancelable: false
        }));
    }

    /**
     * @returns {String}
     */
    getHost() {
        //BC: Remove it if you are sure it not used more
        if (window.my_hop_host !== undefined) {


            return window.my_hop_host;
        }

        return this._getHostFromUrl(this._getScriptUrl());
    }

    /**
     * @returns {int|null}
     */
    getVariantId() {
        const contentVariantMeta = document.querySelector('meta[property="contentVariantId"]');

        if (contentVariantMeta && contentVariantMeta.hasAttribute('content')) {
            const variantId = contentVariantMeta.getAttribute('content');
            return parseInt(variantId);
        }

        return null;
    }

    /**
     * @returns {string|null}
     */
    getFormsQueryString() {
        const webFormQueryStringParts = [];
        const webFormScripts = document.querySelectorAll('script[id*="form-"]');
        webFormScripts.forEach(function (webFormScript) {
            let formId = webFormScript.getAttribute('id');
            if (!!formId) {
                formId = formId.replace('form-', '');
                webFormQueryStringParts.push('formIds[]=' + encodeURIComponent(formId));
            }

        });

        if (webFormQueryStringParts.length > 0) {
            return webFormQueryStringParts.join('&');
        }

        return null;
    }
}

class UtmClickProvider {
    register(hostFromUrl, clientId) {
        const search = window.location.search;
        const host = window.location.host;
        const url = window.location.href;

        let clickScriptUrl = 'https://' + hostFromUrl + '/click/' + search + (search ? '&' : '?')
            + 'host=' + encodeURIComponent(host)
            + '&client_id=' + encodeURIComponent(clientId)
            + '&httpReferer=' + encodeURIComponent(url);

        if (this._isSafari()) {
            const cookies = this._getUtmCookies();
            for (const [cookieName, cookieValue] of Object.entries(cookies)) {
                if (typeof cookieValue === 'undefined') {
                    continue;
                }

                clickScriptUrl += `&utmClicks[${cookieName}]=${cookieValue}`;
            }
        }

        this._loadScript(clickScriptUrl, true);
    }

    _getUtmCookies() {
        return {
            JustClickUtm: Cookie.get('JustClickUtm'),
            JustClickUtmFirst: Cookie.get('JustClickUtmFirst'),
            JustClickAff: Cookie.get('JustClickAff'),
            JustClickAffFirst: Cookie.get('JustClickAffFirst'),
        };
    }

    _isSafari() {
        return navigator.userAgent.indexOf('Safari') !== -1
            && navigator.userAgent.indexOf('Chrome') === -1;
    }

    _loadScript(scriptUrl, async = false) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = async;
        script.src = scriptUrl;

        const body = document.getElementsByTagName('body')[0];
        body.appendChild(script);
    }
}

const clickContextProvider = new ClickContextProvider();
const utmClickProvider = new UtmClickProvider();
const iubendaProvider = new IubendaProvider();

// Инициализация скрипта
if (!window.jcClickIsInit) {
    window.jcClickIsInit = true;

    setupIubendaCallbacks();

    document.addEventListener('readystatechange', () => {
        if (document.readyState === 'interactive') {
            setupIubendaCallbacks();
        }
    });

    document.addEventListener('DOMContentLoaded', async () => {
        // Если Iubenda подключена:
        // handleTrackClick откладывается до принятия клиентом оферты (IubendaProvider.setConsent),
        // запускаются только обработчики, чьи действия не приводят к установке кук
        if (iubendaProvider.hasIubenda()) {
            setupIubendaCallbacks();
            executeDeferredHandlers();

            iubendaProvider.startWaitConsentCookie();
        } else {
            setupClickJs();
        }
    });
}

function setupIubendaCallbacks() {
    if (typeof _iub === 'undefined') {
        return false
    }

    if (_iub.csConfiguration && !!_iub.csConfiguration.callback === false) {
        _iub.csConfiguration.callback = {
            onConsentRead: () => {
                // Дополнительная проверка добавлена из за того, что при повторной загрузке скрипт даже при отказе вызывает метод onConsentRead
                if (_iub.cs.api.isConsentGiven()) {
                    iubendaProvider.setConsent(true);
                } else {
                    iubendaProvider.setConsent(false);
                }
            },
            onPreferenceNotNeeded: () => {
                iubendaProvider.setConsent(true);
            },
            onConsentRejected: () => {
                iubendaProvider.setConsent(false);
            },
            onError: (error) => {
                console.warn('Iubenda error', error);

                iubendaProvider.setConsent(false);
            },
            onFatalError: (error) => {
                console.warn('Iubenda fatal error', error);

                iubendaProvider.setConsent(false);
            },
        }
    }
}

/**
 * @returns {Promise<void>}
 */
async function setupClickJs() {
    const clientId = clickContextProvider.getClickClientId();
    if (typeof clientId !== 'undefined') {
        handleTrackClick(clientId);
        executeDeferredHandlers();
    } else {
        await handleTrackClick(clientId)
            .finally(executeDeferredHandlers);
    }
}

/**
 * handleTrackClick
 * @param clientId
 * @returns {Promise<boolean>}
 */
async function handleTrackClick(clientId) {
    // Не отслеживаем перезагрузку страницы в результате отправки формы
    if (window.location.hash !== '') {
        if (document.referrer + window.location.hash === window.location.href) {
            return false;
        }
    }

    const hostFromUrl = clickContextProvider.getHost();
    const variantId = clickContextProvider.getVariantId();
    const sourceClickId = sessionStorage.getItem('clicks.source_click_id');
    const referrer = document.referrer;
    const url = window.location.href;
    const formsQueryString = clickContextProvider.getFormsQueryString();

    if (clientId !== undefined) {
        utmClickProvider.register(hostFromUrl, clientId);
    }

    const trackUrl = 'https://' + hostFromUrl + '/track/click' +
        '?url=' + encodeURIComponent(url) +
        '&referrer=' + encodeURIComponent(referrer) +
        '&host=' + encodeURIComponent(window.location.host) +
        (clientId !== undefined ? '&client_id=' + encodeURIComponent(clientId) : '') +
        (variantId !== null ? '&variant_id=' + parseInt(variantId) : '') +
        (sourceClickId !== null ? '&source_click_id=' + sourceClickId : '') +
        (formsQueryString !== null ? '&' + formsQueryString : '');

    const response = await ajaxSend(trackUrl);

    if (response.status === true) {
        if (clientId === undefined) {
            utmClickProvider.register(hostFromUrl, response.data.client_id);
        }

        Cookie.set('clicks_client_id', response.data.client_id, { expires: 7 }); // Неделя
        window.sessionStorage.setItem('clicks.source_click_id', response.data.click);

        clickContextProvider.dispatchClientId(response.data.client_id);

        if (response.data.set_cookie_url) {
            ajaxSend(response.data.set_cookie_url)
        }
    }
}

/**
 * ajaxSend
 * @param url
 * @param method
 * @returns {Promise<unknown>}
 */
function ajaxSend(url, method) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open(method || 'GET', url, true);

        xhr.withCredentials = true;
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

        xhr.onreadystatechange = () => { // (3)
            if (xhr.readyState !== 4) {
                return;
            }

            if (xhr.status === 200 && xhr.getResponseHeader('Content-Type') === 'application/json') {
                const response = JSON.parse(xhr.responseText);
                resolve(response);
            } else {
                reject(Error(xhr.statusText))
            }
        };

        xhr.send();
    });
}

/**
 * executeDeferredHandlers
 */
function executeDeferredHandlers() {
    handleLmsClientBlocks();
    handleFunnelsHref();

    window.addEventListener('hashchange', handleFunnelsHref);
}

/**
 * handleLmsClientBlocks
 * @returns {Promise<void>}
 */
async function handleLmsClientBlocks() {
    const elements = document.getElementsByClassName('jc-client-block');
    const elementsByName = {};
    const queryParameters = [];

    for (const element of elements) {
        const name = element.getAttribute('data-name');
        elementsByName[name] = element;
        queryParameters.push('blocks[]=' + encodeURIComponent(name));
    }

    const hostFromUrl = clickContextProvider.getHost();
    const response = await ajaxSend('//' + hostFromUrl + '/lms/api/client-blocks?' + queryParameters.join('&'));

    if (response.status === true) {
        for (const blockName of Object.keys(response.data.blocks)) {
            const content = response.data.blocks[blockName];
            const element = elementsByName[blockName];

            if (element === undefined || content === null) {
                continue;
            }

            element.innerHTML = content;
        }
    }
}

/**
 * handleFunnelsHref
 */
function handleFunnelsHref() {
    const href = window.location.hash;
    if (href.toLowerCase().indexOf('#nextpage') !== -1) {
        handleNextStep();
    } else if (href.toLowerCase().indexOf('#upsell_yes') !== -1) {
        handleUpsellYes();
    } else if (href.toLowerCase() === '#upsell_no') {
        handleUpsellNo();
    }
}

/**
 * handleNextStep
 * @returns {Promise<boolean>}
 */
async function handleNextStep() {
    const hostFromUrl = clickContextProvider.getHost();

    const resolveRedirectUrl = '//' + hostFromUrl + '/funnels/next-page' +
        '?url=' + encodeURIComponent(window.location.href);

    const response = await ajaxSend(resolveRedirectUrl);

    if (response.status === true) {
        window.location.replace(response.location);
        return false;
    }

    return false;
}

/**
 * handleUpsellYes
 * @returns {Promise<boolean>}
 */
async function handleUpsellYes() {
    const hostFromUrl = clickContextProvider.getHost();

    let urlParams = new URLSearchParams(window.location.search);

    const resolveRedirectUrl = '//' + hostFromUrl + '/funnels/upsell-yes/' + urlParams.get('billId') +
        '?url=' + encodeURIComponent(window.location.href);

    const response = await ajaxSend(resolveRedirectUrl);

    if (response.status === true) {
        window.location.replace(response.location);

        return false;
    }

    return false;
}

/**
 * handleUpsellNo
 * @returns {Promise<boolean>}
 */
async function handleUpsellNo() {
    const hostFromUrl = clickContextProvider.getHost();

    let urlParams = new URLSearchParams(window.location.search);

    const resolveRedirectUrl = '//' + hostFromUrl + '/funnels/upsell-no/' + urlParams.get('billId') +
        '?url=' + encodeURIComponent(window.location.href);

    const response = await ajaxSend(resolveRedirectUrl);

    if (response.status === true) {
        window.location.replace(response.location);

        return false;
    }

    return false;
}
